import { useState } from 'react';
import { Repeat, Trash } from 'lucide-react';
import type { Task } from '../api/tasks.js';
import { Badge } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { useProjects } from '../stores/projects.js';
import { useTasks } from '../stores/tasks.js';
import { useUi } from '../stores/ui.js';
import { cn } from '../lib/cn.js';
import { teinte } from '../lib/teinte.js';
import { formatShort, todayInParis } from '../lib/dates.js';
import { formatRrule } from '../lib/rrule-format.js';
import { TexteLie } from './TexteLie.js';
import { texteBrut } from '../lib/linkify.js';

// `.ligne` du kit : la coche (une action — la tâche terminée quitte la liste), le titre sur deux
// lignes au plus, son détail, et la suppression qui n'apparaît qu'au survol.
export function TaskRow({
  task,
  hideProject = false,
  compact = false,
  showSubtitle = false,
  hideToday = false,
}: {
  task: Task;
  hideProject?: boolean;
  compact?: boolean;
  showSubtitle?: boolean;
  /** Dans la vue Today, « Today » sur chaque ligne ne dit rien : seul le retard s'écrit. */
  hideToday?: boolean;
}): React.ReactElement {
  const project = useProjects((s) => s.find(task.projectId));
  const { complete, remove } = useTasks();
  const openTaskDetail = useUi((s) => s.openTaskDetail);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const subtaskCount = task.subtasks.length;
  const subtaskDone = task.subtasks.filter((s) => s.done).length;

  const rawDateLabel = task.dueDate ? formatShort(task.dueDate) : null;
  const dateLabel = hideToday && rawDateLabel === 'Today' ? null : rawDateLabel;
  const overdue = task.dueDate !== null && task.dueDate < todayInParis();
  const rruleLabel = formatRrule(task.rrule);
  const hasSubtitle = showSubtitle && (dateLabel || rruleLabel);

  return (
    <div
      onClick={() => openTaskDetail(task.taskId)}
      className={cn('ligne ligne-cliquable', compact && 'ligne-dense')}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          complete(task.taskId);
        }}
        // Teintée par son projet (Braise 2) : le projet se lit sur la ligne sans étiquette.
        className="coche coche-teintee"
        style={teinte(project?.color)}
        aria-label="Complete"
      />
      <div className="ligne-corps">
        <span className="ligne-titre">
          <TexteLie text={task.title} />
        </span>
        {hasSubtitle && (
          <span className="ligne-detail">
            {dateLabel && (
              <span
                className={cn(
                  dateLabel === 'Today' && 'text-now font-medium',
                  overdue && 'text-error font-medium'
                )}
              >
                {dateLabel}
              </span>
            )}
            {dateLabel && rruleLabel && ' · '}
            {rruleLabel}
          </span>
        )}
      </div>

      {subtaskCount > 0 && (
        <span className="ligne-repere">
          {subtaskDone}/{subtaskCount}
        </span>
      )}

      {!showSubtitle && task.rrule && (
        <Repeat size={14} className="shrink-0 text-muted" aria-label="Recurring" />
      )}

      {!hideProject && project && (
        <Badge color={project.color} className="shrink-0">
          {project.name}
        </Badge>
      )}

      {!compact && (
        <Button
          variant="quiet"
          size="icon-small"
          onClick={(e) => {
            e.stopPropagation();
            setConfirmingDelete(true);
          }}
          className="ligne-action hidden md:inline-flex"
          aria-label="Delete"
        >
          <Trash size={16} aria-hidden />
        </Button>
      )}

      <ConfirmDialog
        open={confirmingDelete}
        title="Delete this task?"
        message={texteBrut(task.title)}
        confirmLabel="Delete"
        onConfirm={() => {
          setConfirmingDelete(false);
          void remove(task.taskId);
        }}
        onCancel={() => setConfirmingDelete(false)}
      />
    </div>
  );
}
